import React, { useEffect, useState } from 'react';
import { Modal, Button, Input, Form, message, Tooltip } from 'antd';
import styles from './s.module.less';
import { IClosedFunnelQuestion } from 'types/operation';
import { updateClosedFunnelQuestion } from 'api/operation/closedFunnel';
import capitalize from 'lodash/capitalize';
import SwitchWithText from './switchWithText';
import { QuestionCircleOutlined } from '@ant-design/icons';
import SelfPayPageBg from './selfPayPageBg.png';

interface QuestionEditModalProps {
    question: IClosedFunnelQuestion;
    open: boolean;
    onSuccess: () => void;
    onCancel: () => void;
}

const QuestionEditModal: React.FC<QuestionEditModalProps> = ({
    question,
    open,
    onSuccess,
    onCancel,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const statusWatched = Form.useWatch('status', form);
    const selfPayPageWatched = Form.useWatch('showSelfPayPage', form);

    useEffect(() => {
        form.setFieldsValue({
            status: question.status === 'A',
            name: question.name,
            subtext: question.subtext,
        });
        const metaData = question.metaData ? JSON.parse(question.metaData) : {};
        if (question.type === 'PAYMENT') {
            form.setFieldsValue({
                showSelfPayPage: metaData.showSelfPayPage !== undefined ? metaData.showSelfPayPage : true,
            });
        }
    }, [form, question]);

    const handleSave = () => {
        if (question?.id) {
            form.validateFields().then(async (values) => {
                setLoading(true);
                if (question.type === 'PAYMENT') {
                    values.metaData = JSON.stringify({
                        showSelfPayPage: values.showSelfPayPage,
                    });
                    delete values.showSelfPayPage;
                }
                try {
                    const res = await updateClosedFunnelQuestion(question.id, {
                        ...values,
                        status: values.status ? 'A' : 'D',
                    });
                    if (res && !res.error) {
                        onSuccess?.();
                    } else {
                        message.error(res?.message);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    setLoading(false);
                }
            });
        }
    };

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            className={styles.modal}
            width={680}
            centered
            destroyOnClose
        >
            <div className={styles.header}>
                <span className={styles.title}>{capitalize(question.type)}</span>
                <div className={styles.headerBtns}>
                    <Button className={styles.cancelBtn} onClick={onCancel}>Cancel</Button>
                    <Button className={styles.saveBtn} type="primary" onClick={handleSave} loading={loading}>Save changes</Button>
                </div>
            </div>
            <div className={styles.requiredTip}><span className={styles.red}>*</span> Indicates required fields</div>
            <Form
                form={form}
                layout="vertical"
            >
                <Form.Item
                    label={<span className={styles.label}>Status</span>}
                    name="status"
                    valuePropName="checked"
                // rules={[{ required: true, message: 'Please select status' }]}
                >
                    <SwitchWithText text={statusWatched ? 'Active' : 'Inactive'} />
                </Form.Item>
                <Form.Item
                    label={<span className={styles.label}>Question</span>}
                    name="name"
                    rules={[{ required: true, message: 'Please input question' }, { max: 50, message: 'Max 50 characters' }]}
                >
                    <Input className={styles.input} maxLength={50} showCount />
                </Form.Item>
                <Form.Item
                    label={<span className={styles.label}>Subtext</span>}
                    name="subtext"
                    rules={[{ required: true, message: 'Please input subtext' }, { max: 80, message: 'Max 80 characters' }]}
                >
                    <Input className={styles.input} maxLength={80} showCount />
                </Form.Item>
                {
                    question.type === 'PAYMENT' && (
                        <>
                            <Form.Item
                                label={
                                    <span className={styles.label}>
                                        <span className="mr-2">Self-pay landing page</span>
                                        <Tooltip title="Patients who choose self-pay will see a landing page with transparent pricing highlights before moving forward.">
                                            <QuestionCircleOutlined />
                                        </Tooltip>
                                    </span>
                                }
                                name="showSelfPayPage"
                                valuePropName="checked"
                            >
                                <SwitchWithText text={selfPayPageWatched ? 'Active' : 'Inactive'} />
                            </Form.Item>
                            <img src={SelfPayPageBg} alt="Self Pay Page" className={styles.selfPayPageBg} />
                        </>
                    )
                }
            </Form>
        </Modal>
    );
};

export default QuestionEditModal;
